import { useState, useEffect } from 'react';
import './astyles/LocationsList.css';

interface Location {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  created_by?: number;
  is_active: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

export default function LocationsList() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newLocation, setNewLocation] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/locations`);
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      } else {
        setError('Failed to fetch locations');
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLocation.trim()) return;

    setAdding(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1' // Admin user ID
        },
        body: JSON.stringify({
          name: newLocation.trim(),
          description: newDescription.trim() || null
        })
      });

      if (response.ok) {
        const newLocationData = await response.json();
        setLocations(prev => [...prev, newLocationData].sort((a, b) => a.name.localeCompare(b.name)));
        setNewLocation('');
        setNewDescription('');
        setShowAddForm(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add location');
      }
    } catch (error) {
      console.error('Error adding location:', error);
      setError('Failed to add location');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="locations-list">
        <div className="loading">Loading locations...</div>
      </div>
    );
  }

  return (
    <div className="locations-list">
      <div className="list-header">
        <h2>Locations Management</h2>
        <button
          className="add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add New Location'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddLocation} className="add-form">
          <div className="form-group">
            <label htmlFor="locationName">Location Name:</label>
            <input
              id="locationName"
              type="text"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              placeholder="Enter location name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="locationDescription">Description (optional):</label>
            <textarea
              id="locationDescription"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Enter location description"
              rows={3}
            />
          </div>
          <div className="form-actions">
            <button type="submit" disabled={adding || !newLocation.trim()}>
              {adding ? 'Adding...' : 'Add Location'}
            </button>
          </div>
        </form>
      )}

      <div className="locations-grid">
        {locations.length === 0 ? (
          <div className="no-data">No locations found</div>
        ) : (
          locations.map(location => (
            <div key={location.id} className="location-card">
              <div className="location-header">
                <h3>{location.name}</h3>
                <span className="location-id">#{location.id}</span>
              </div>
              {location.description && (
                <p className="location-description">{location.description}</p>
              )}
              <div className="location-meta">
                <span className="location-status">
                  {location.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="location-date">
                  Added: {new Date(location.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="list-footer">
        <p>Total Locations: {locations.length}</p>
      </div>
    </div>
  );
}
